/**
 * 字体图标容错自救（Font Rescue）
 *
 * 背景：@font-face 字体一旦网络请求失败（发布窗口 404 / 早高峰回源超时 / 网关拦截），
 *       浏览器不会降级到 src 列表中的下一个格式，也不会在本次会话内重试，
 *       依赖该字体的图标会永久保持 fallback 字形（豆腐块/X），只有刷新才可能恢复。
 *
 * 机制：
 *   1. 预加载：应用启动后主动 load 图标字体（等效 preload，提前进入加载队列）；
 *   2. 自愈：字体请求失败处于 error 终态后，从 CSSOM 读出该 family 的 @font-face
 *      src，用全新的 FontFace 对象重新发起请求（旧对象已终态不会再发请求），
 *      加载成功后浏览器自动应用到页面；失败则按递增间隔重试，最多 3 次。
 */
const TARGET_FONTS = [
	{ family: 'iconfont', spec: '16px iconfont' },
	{ family: 'element-icons', spec: '16px element-icons' },
	{ family: 'FontAwesome', spec: '16px FontAwesome' }
];

const MAX_ATTEMPTS = 3;
const RETRY_DELAYS = [2000, 4000, 8000];
const rescuing = new Set();

// 从 CSSOM 读取指定 family 的 @font-face src（浏览器已解析为绝对 URL，可直接复用）
function getFontFaceSrc(family) {
	const sheets = document.styleSheets || [];
	for (let i = 0; i < sheets.length; i++) {
		let rules;
		try {
			rules = sheets[i].cssRules;
		} catch (e) {
			continue; // 跨域样式表（如阿里播放器 CSS）无法读取，跳过
		}
		if (!rules) continue;
		for (let j = 0; j < rules.length; j++) {
			const rule = rules[j];
			if (rule.type === CSSRule.FONT_FACE_RULE) {
				const fam = (rule.style.getPropertyValue('font-family') || '').replace(/['"]/g, '');
				if (fam.indexOf(family) !== -1) {
					return rule.style.getPropertyValue('src');
				}
			}
		}
	}
	return null;
}

// 用新的 FontFace 对象重新发起字体请求（绕过已处于 error 终态的旧对象）
function reloadFontFace(family, src) {
	return new Promise((resolve, reject) => {
		let face;
		try {
			face = new FontFace(family, src, {});
		} catch (e) {
			reject(e);
			return;
		}
		document.fonts.add(face);
		face
			.load()
			.then(() => resolve(true))
			.catch(err => reject(err));
	});
}

function rescueFont(family, attempt) {
	const src = getFontFaceSrc(family);
	if (!src) {
		rescuing.delete(family);
		return; // CSSOM 中找不到声明，无法自愈
	}
	reloadFontFace(family, src)
		.then(() => {
			rescuing.delete(family);
		})
		.catch(() => {
			if (attempt >= MAX_ATTEMPTS) {
				rescuing.delete(family);
				return;
			}
			const delay = RETRY_DELAYS[attempt - 1] || RETRY_DELAYS[RETRY_DELAYS.length - 1];
			setTimeout(() => rescueFont(family, attempt + 1), delay);
		});
}

function tryRescue(family) {
	if (rescuing.has(family)) return;
	rescuing.add(family);
	rescueFont(family, 1);
}

export function initFontRescue() {
	if (!document.fonts || typeof document.fonts.load !== 'function') return;

	TARGET_FONTS.forEach(item => {
		// 1. 预加载：主动触发加载，页面即使暂未用到该字体也会提前进入加载队列
		document.fonts
			.load(item.spec)
			.then(() => {
				// 2. 加载成功，无需处理
			})
			.catch(() => {
				// 3. 加载失败（error 终态）→ 自愈重试
				tryRescue(item.family);
			});

		// 4. 兜底检查：兼容 load() 对未使用字体立即 resolve 的浏览器差异，
		//    4 秒后仍不可用则同样触发自愈
		setTimeout(() => {
			if (!document.fonts.check(item.spec)) {
				tryRescue(item.family);
			}
		}, 4000);
	});
}
